// Логиката на редовете на работния лист „Инвентаризация" (изнесена от
// таблицата, за да са и двете под 500 реда): наличностите към датата, FIFO
// цената на липсите (`useStocktakeFifoPrices`), двупосочните изчисления при
// излишък (ед. цена ↔ стойност, нето), задължителната цена на излишък без
// източник (`priceMissing`), взаимозамените, трите блока и филтрите на изгледа
// (`buildStocktakeView`) и сумите. Филтрите режат САМО `displayRows` —
// `countedRows`, сумите и `buildLines` тръгват от `allRows`, затова скрит ред
// не изпада от записа. Приготвените рецепти (`inRecipesByProduct`) се прибавят
// към намереното на съставките им — разликата, стойността, блоковете и
// статусът са по насипно + в рецепти, а `buildLines` праща само насипното.
import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import type {
  CreateStocktakeLine,
  InterchangeGroup,
  LastPrice,
  Product,
  StockBalance,
} from "../../../../api/inventory";
import {
  DIFF_EPSILON,
  buildSubstitutionProposals,
  diffKindOf,
  emptyStocktakeRow,
  formatPrice,
  formatQtyInput,
  isRowCounted,
  parseDecimal,
  parseQtyExpression,
  substitutionAdjustments,
  type StocktakeComputedRow,
  type StocktakeRowDraft,
} from "./stocktakeWorksheetShared";
import { formatUnitPrice } from "../../components/numbers";
import { useStocktakeFifoPrices } from "./useStocktakeFifoPrices";
import { roundQuantity, type InRecipesOfProduct } from "./stocktakeRecipes";
import {
  buildStocktakeView,
  stocktakeProductGroups,
  type StocktakeStatusFilter,
} from "./stocktakeView";

// Числовите клетки на реда (колоните 0–2 в решетката и картите, виж
// STOCKTAKE_COLS): „Намерено" · „Ед. цена" · „Стойност" (нето на разликата)
export type NumericCol = "counted" | "price" | "net";

type Params = {
  products: Product[];
  warehouseId: number | null;
  balanceByProduct: Map<number, StockBalance>;
  lastPriceByProduct: Map<number, LastPrice>;
  // Запазеното „търсено" при редакция — след запис живият баланс вече е равен
  // на преброеното, затова очакваното ПРЕДИ инвентаризацията идва от записа.
  savedSystemByProduct: Map<number, number>;
  vatRegistered: boolean;
  decimalsOf: (productId: number) => number;
  onTouch: () => void; // вдига флага „незапазени промени" + чернова
  // Взаимозамените се смятат ТУК, защото приложените двойки нетират разликите
  // на редовете още при натискането на „Замени" (виж `substitutionAdjustments`).
  interchangeGroups: InterchangeGroup[];
  appliedDonorByRecipient: Map<number, number>;
  // Филтърът над таблицата: скрий редовете без наличност („Търсено" = 0)
  hideZeroStock: boolean;
  // Продукти, които филтърът НЕ крие, дори да са без наличност — избраните от
  // търсачката (иначе кликът води до ред, който го няма на екрана).
  revealedProductIds: Set<number>;
  // Филтърът по група продукти (чиповете над таблицата); null = всички
  groupFilter: string | null;
  // Избраните в „Целият списък" и новосъздадените (в реда на избора): блокът
  // „За броене" най-отгоре, докато не бъдат преброени; групата и „Скрий без
  // наличност" не ги крият
  pinnedProductIds?: number[];
  // Чиповете Всички / За броене / Преброени / Непреброени / С разлика (само
  // изгледът)
  statusFilter?: StocktakeStatusFilter;
  // Последните доставни цени са заредени. Докато не са, липсата на цена не е
  // доказана и излишъкът не се спира (`priceMissing` = false).
  priceSourcesReady?: boolean;
  // Приготвените рецепти: продукт → частта му в тях (useStocktakeRecipes)
  inRecipesByProduct?: Map<number, InRecipesOfProduct>;
};

// Постоянни празни стойности — нов `[]`/`Map` на всеки рендер би преизчислявал
// всички редове и изгледа
const NO_PINNED: number[] = [];
const NO_RECIPES = new Map<number, InRecipesOfProduct>();

export function useStocktakeRowsActions({
  products,
  warehouseId,
  balanceByProduct,
  lastPriceByProduct,
  savedSystemByProduct,
  vatRegistered,
  decimalsOf,
  onTouch,
  interchangeGroups,
  appliedDonorByRecipient,
  hideZeroStock,
  revealedProductIds,
  groupFilter,
  pinnedProductIds = NO_PINNED,
  statusFilter = "all",
  priceSourcesReady = true,
  inRecipesByProduct = NO_RECIPES,
}: Params) {
  const [rows, setRows] = useState<Record<number, StocktakeRowDraft>>({});
  // УСТАНОВЕНОТО намерено по продукт: ключът = редът е в блока „Преброени",
  // стойността решава статуса му за чиповете (С разлика). Обновява се при
  // НАПУСКАНЕ на клетката, не при всяко натискане — иначе редът отскача (или
  // изчезва под филтъра), докато се пише.
  const [settledCountedById, setSettledCountedById] = useState<Map<number, number>>(
    new Map(),
  );

  // „Търсено": наличността към датата; отрицателна се чете като 0 (физически
  // не може да имаш под 0 на рафта).
  const systemQtyOf = useCallback(
    (productId: number) =>
      Math.max(
        0,
        savedSystemByProduct.get(productId) ??
          balanceByProduct.get(productId)?.quantity ??
          0,
      ),
    [savedSystemByProduct, balanceByProduct],
  );

  // ─── FIFO цена на липсите (тегли се лениво само за тях) ──────────────────
  const fifoUnitPrice = useStocktakeFifoPrices({ warehouseId, rows, systemQtyOf });

  // ─── Един пресметнат ред ─────────────────────────────────────────────────
  // `adjustment` е ефектът на приложените взаимозамени върху разликата (виж
  // `substitutionAdjustments`) — така редът показва това, което ще се запише.
  const computeRow = useCallback(
    (product: Product, adjustment = 0): StocktakeComputedRow => {
      const draft = rows[product.id] ?? emptyStocktakeRow();
      const balance = balanceByProduct.get(product.id);
      const systemQty = systemQtyOf(product.id);
      // Намереното = насипното (полето) + частта в приготвените рецепти (4
      // знака, както BE). Без насипно редът не е преброен, рецепти или не.
      const inRecipes = inRecipesByProduct.get(product.id);
      const inRecipesQty = inRecipes?.total ?? 0;
      const looseQty = isRowCounted(draft) ? parseQtyExpression(draft.counted) : null;
      const counted =
        looseQty == null ? null : inRecipesQty > 0 ? roundQuantity(looseQty + inRecipesQty) : looseQty;
      const rawDifference = counted == null ? 0 : counted - systemQty;
      const rawKind = counted == null ? "equal" : diffKindOf(rawDifference);
      const difference = counted == null ? 0 : rawDifference + adjustment;
      const kind = counted == null ? "equal" : diffKindOf(difference);
      // Източникът на цена на излишъка — същият като на бекенда: последната
      // доставна цена на продукта в ТОЗИ склад (`GET /last-prices`, всеки
      // доставчик); без нея и без написана цена бекендът записва 0.
      const lastPriceRecord = lastPriceByProduct.get(product.id);
      const lastPrice = lastPriceRecord?.unitPrice ?? 0;
      const weightedAvgPrice = balance?.weightedAvgPrice ?? null;
      // Ед. цената (винаги нето) се отнася САМО за разликата:
      //  • излишък → цената на новата партида (ръчна / последна доставна);
      //  • липса → FIFO по изписваните партиди (докато се зареждат — ср. цена).
      const priceAuto = draft.price === "";
      const priceMissing =
        kind === "surplus" && priceAuto && priceSourcesReady && lastPriceRecord == null;
      const unitPrice =
        kind === "surplus"
          ? priceAuto
            ? lastPrice
            : parseDecimal(draft.price)
          : kind === "shortage"
            ? (fifoUnitPrice(product.id, -difference) ??
              weightedAvgPrice ??
              lastPrice)
            : 0;
      const vatRate = product.vatRate ?? 0;
      const vatFactor = vatRegistered && vatRate ? 1 + vatRate / 100 : 1;
      // Написаната стойност на излишъка (нето; бруто — само от стара чернова)
      // се пази ТОЧНО както е написана: цената се извежда от нея (до 5 знака),
      // а не обратно — иначе 2 500 бр. за 2.10 даваха стойност 2.00.
      const typedNet = kind === "surplus" && draft.net !== "" ? parseDecimal(draft.net) : null;
      const typedGross =
        kind === "surplus" && typedNet == null && draft.gross !== "" ? parseDecimal(draft.gross) : null;
      const differenceNet =
        counted == null
          ? 0
          : typedNet ?? (typedGross != null ? typedGross / vatFactor : difference * unitPrice);
      const differenceGross = typedGross ?? differenceNet * vatFactor;
      return {
        product,
        draft,
        decimals: decimalsOf(product.id),
        unit: balance?.unit ?? product.unit ?? "",
        systemQty,
        countedQty: counted,
        looseQty,
        inRecipesQty,
        recipeParts: inRecipes?.parts ?? [],
        needsLooseCount: inRecipesQty > 0 && looseQty == null,
        rawDifference,
        rawKind,
        difference,
        kind,
        unitPrice,
        priceEditable: kind === "surplus",
        priceAuto,
        priceMissing,
        differenceNet,
        differenceGross,
        // Полетата показват написаното; щом няма написано — изведеното (при
        // липсващ източник и цената, и стойността са ПРАЗНИ — „0.00" би
        // изглеждало попълнено). Цената е `formatUnitPrice`, сумата — с 2 знака.
        priceInput: priceMissing ? "" : priceAuto ? formatUnitPrice(unitPrice) : draft.price,
        netInput: draft.net !== "" ? draft.net : priceMissing ? "" : formatPrice(differenceNet),
      };
    },
    [
      rows,
      balanceByProduct,
      systemQtyOf,
      lastPriceByProduct,
      fifoUnitPrice,
      vatRegistered,
      decimalsOf,
      priceSourcesReady,
      inRecipesByProduct,
    ],
  );

  // ─── Всички редове по азбучен ред + секциите ─────────────────────────────
  // Сортирането е по продукти (не по редове) → не се повтаря на всяко писане.
  const sortedProducts = useMemo(
    () => [...products].sort((a, b) => a.name.localeCompare(b.name, "bg")),
    [products],
  );
  // Редовете БЕЗ замените — от тях се строят предложенията за покриване (иначе
  // двойката би изчезнала в мига, в който я приложиш).
  const rawRows = useMemo(
    () => sortedProducts.map((product) => computeRow(product)),
    [sortedProducts, computeRow],
  );
  const proposals = useMemo(
    () =>
      buildSubstitutionProposals(
        rawRows.filter((row) => isRowCounted(row.draft)),
        interchangeGroups,
      ),
    [rawRows, interchangeGroups],
  );
  // Приложените двойки нетират разликите ВЕДНАГА (както ще ги запише бекендът):
  // получателят си връща покритото, донорът дава своя еквивалент.
  const adjustments = useMemo(
    () => substitutionAdjustments(proposals, appliedDonorByRecipient),
    [proposals, appliedDonorByRecipient],
  );
  const allRows = useMemo(() => {
    if (adjustments.size === 0) return rawRows;
    return rawRows.map((row) => {
      const adjustment = adjustments.get(row.product.id);
      return adjustment ? computeRow(row.product, adjustment) : row;
    });
  }, [rawRows, adjustments, computeRow]);
  // Подредбата на екрана: първо закачените „За броене", после преброените
  // (иначе десетте се губят сред хилядите), после тези с наличност и накрая
  // нулевите; филтрите на изгледа режат САМО показаното (`buildStocktakeView`).
  // Съставките на рецептите без насипно преброено са „За броене" в реда на
  // рецептите (редът на ключовете на `inRecipesByProduct`).
  const recipeIngredientOrder = useMemo(() => [...inRecipesByProduct.keys()], [inRecipesByProduct]);
  const view = useMemo(
    () =>
      buildStocktakeView(allRows, settledCountedById, adjustments, {
        groupFilter,
        hideZeroStock,
        statusFilter,
        revealedProductIds,
        pinnedProductIds,
        recipeIngredientOrder,
      }),
    [
      allRows,
      settledCountedById,
      adjustments,
      groupFilter,
      hideZeroStock,
      statusFilter,
      revealedProductIds,
      pinnedProductIds,
      recipeIngredientOrder,
    ],
  );
  const { displayRows } = view;
  // Групите за чиповете (по целия каталог)
  const productGroups = useMemo(() => stocktakeProductGroups(products), [products]);
  // Редът, по който вървят стрелките ↑/↓ (както се вижда на екрана)
  const visibleProductIds = useMemo(
    () => displayRows.map((row) => row.product.id),
    [displayRows],
  );
  const rowByProduct = useMemo(
    () => new Map(allRows.map((row) => [row.product.id, row])),
    [allRows],
  );

  // ─── Промени по клетките ─────────────────────────────────────────────────
  const patchRow = useCallback(
    (productId: number, updater: (row: StocktakeRowDraft) => void) => {
      setRows((prev) => {
        const next = { ...(prev[productId] ?? emptyStocktakeRow()) };
        updater(next);
        return { ...prev, [productId]: next };
      });
      onTouch();
    },
    [onTouch],
  );

  // Стойността на разликата (нето) → обратно смятане на ед. цената на новата
  // партида (стойност / излишъка) с ЦЕНОВАТА точност (`formatUnitPrice`, не 2
  // знака). Само при ИЗЛИШЪК; при липса цената е FIFO. Изтрита стойност →
  // авто цена.
  const priceFromDifferenceValue = (productId: number, rawValue: string): string => {
    const row = rowByProduct.get(productId);
    if (!row || row.difference <= DIFF_EPSILON || rawValue === "") return "";
    return formatUnitPrice(parseDecimal(rawValue) / row.difference);
  };

  const onCellChange = (
    productId: number,
    col: NumericCol,
    rawValue: string,
  ) => {
    // Числовите клетки приемат САМО цифри, запетая и точка; „Намерено" пуска
    // и „+“/„−“, за да се напише израз („10+5“ → 15, „15-2“ → 13).
    const value = rawValue.replace(
      col === "counted" ? /[^0-9.,+-]/g : /[^0-9.,]/g,
      "",
    );
    patchRow(productId, (row) => {
      if (col === "counted") {
        row.counted = value;
        // Стойността се извежда наново от новата разлика
        row.net = "";
        row.gross = "";
      } else if (col === "price") {
        row.price = value;
        row.net = "";
        row.gross = "";
      } else if (col === "net") {
        // Написаното остава (и след напускане); цената го следва
        row.net = value;
        row.gross = "";
        row.price = priceFromDifferenceValue(productId, value);
      }
    });
  };

  // Напускане на клетка: нормализира написаното (количество по мерната
  // единица, цена до 5 знака, сума с 2) и решава дали редът минава в блока
  // „Преброени". Написаната Стойност ОСТАВА — сумата е точно написаната, не
  // изведена обратно от закръглената цена; нова цена или ново намерено я
  // връща към изведената (`onCellChange`).
  const onCellBlur = (productId: number, col: NumericCol) => {
    setRows((prev) => {
      const row = prev[productId];
      if (!row) return prev;
      const next = { ...row };
      if (col === "counted" && next.counted !== "")
        next.counted = formatQtyInput(
          parseQtyExpression(next.counted),
          decimalsOf(productId),
        );
      if (col === "price" && next.price !== "")
        next.price = formatUnitPrice(parseDecimal(next.price));
      if (col === "net" && next.net !== "") next.net = formatPrice(parseDecimal(next.net));
      return { ...prev, [productId]: next };
    });
    if (col !== "counted") return;
    // Едва сега (при напускане) редът сменя блока и статуса си.
    const draft = rows[productId] ?? emptyStocktakeRow();
    const settled = isRowCounted(draft) ? parseQtyExpression(draft.counted) : null;
    setSettledCountedById((prev) => {
      if ((prev.get(productId) ?? null) === settled) return prev;
      const next = new Map(prev);
      if (settled == null) next.delete(productId);
      else next.set(productId, settled);
      return next;
    });
  };

  const onBatchChange = (productId: number, value: string) =>
    patchRow(productId, (row) => {
      row.batch = value;
    });
  const onExpiryChange = (productId: number, expiry: string) =>
    patchRow(productId, (row) => {
      row.expiry = expiry;
    });

  // Зареждане наведнъж (редакция / чернова)
  const replaceAllRows = useCallback(
    (nextRows: Record<number, StocktakeRowDraft>) => {
      setRows(nextRows);
      setSettledCountedById(
        new Map(
          Object.entries(nextRows)
            .filter(([, row]) => isRowCounted(row))
            .map(([productId, row]) => [+productId, parseQtyExpression(row.counted)]),
        ),
      );
    },
    [],
  );

  // ─── Изведени стойности ──────────────────────────────────────────────────
  const countedRows = useMemo(
    () => allRows.filter((row) => isRowCounted(row.draft)),
    [allRows],
  );
  const surplusCount = countedRows.filter((r) => r.kind === "surplus").length;
  const shortageCount = countedRows.filter((r) => r.kind === "shortage").length;
  const totalNet = countedRows.reduce((sum, r) => sum + r.differenceNet, 0);
  const totalGross = countedRows.reduce((sum, r) => sum + r.differenceGross, 0);
  // Разбивката на нетото в реда „Общо": излишъците (+) и липсите (−) поотделно
  const sumNetOf = (kind: StocktakeComputedRow["kind"]) =>
    countedRows.reduce((sum, r) => (r.kind === kind ? sum + r.differenceNet : sum), 0);

  // Всеки преброен продукт → ред (включително равните). Ръчна цена + партида/
  // срок се пращат САМО при ИЗЛИШЪК; липсата ползва FIFO на бекенда. `counted`
  // е НАСИПНОТО — бекендът сам прибавя съставките на приготвените рецепти.
  // Съставките без насипно преброено са пречка (`needsLooseCount`).
  const uncountedIngredients = useMemo(
    () =>
      recipeIngredientOrder
        .map((productId) => rowByProduct.get(productId))
        .filter((row): row is StocktakeComputedRow => !!row?.needsLooseCount)
        .map((row) => ({ productId: row.product.id, name: row.product.name })),
    [recipeIngredientOrder, rowByProduct],
  );
  const buildLines = (): CreateStocktakeLine[] =>
    countedRows.map((row) => ({
      productId: row.product.id,
      counted: row.looseQty as number,
      unitPrice:
        row.kind === "surplus" && !row.priceAuto
          ? parseDecimal(row.draft.price)
          : null,
      batchNumber:
        row.kind === "surplus" ? row.draft.batch.trim() || null : null,
      expiryDate:
        row.kind === "surplus" && row.draft.expiry
          ? dayjs(row.draft.expiry).toISOString()
          : null,
    }));

  return {
    rows,
    allRows,
    // редът „Общо" в дъното на решетката
    totals: {
      net: totalNet,
      gross: totalGross,
      counted: countedRows.length,
      surplus: surplusCount,
      shortage: shortageCount,
      surplusNet: sumNetOf("surplus"),
      shortageNet: sumNetOf("shortage"),
    },
    replaceAllRows,
    productGroups,
    // трите блока, показаните редове и броячите на чиповете за статус
    view,
    displayRows,
    countedRows,
    proposals,
    rowByProduct,
    visibleProductIds,
    surplusCount,
    shortageCount,
    totalNet,
    totalGross,
    onCellChange,
    onCellBlur,
    onBatchChange,
    onExpiryChange,
    buildLines,
    uncountedIngredients,
  };
}
